import { useEffect, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
} from '@mui/material';
import { useNotify } from 'react-admin';

import {
    type Receipt,
    type ReceiptCategory,
    type ResponsibleUser,
    updateReceipt,
} from './receiptApi';
import {
    AllocationFields,
    allocationChanges,
    allocationFrom,
    allocationReady,
    type Allocation,
} from './AllocationFields';

type Props = {
    receipt: Receipt | null;
    categories: ReceiptCategory[];
    users: ResponsibleUser[];
    onClose: () => void;
    onSaved: () => void;
};

export function ReceiptAllocationEditDialog({
    receipt,
    categories,
    users,
    onClose,
    onSaved,
}: Props) {
    const notify = useNotify();
    const [allocation, setAllocation] = useState<Allocation>(allocationFrom({}));
    const [merchant, setMerchant] = useState('');
    const [amount, setAmount] = useState('');
    const [transactionDate, setTransactionDate] = useState('');
    const [responsibleUserId, setResponsibleUserId] = useState('');
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!receipt) return;
        setAllocation(allocationFrom(receipt));
        setMerchant(receipt.merchant || '');
        setAmount(receipt.amount || '');
        setTransactionDate(receipt.transaction_date || '');
        setResponsibleUserId(String(receipt.responsible_user_id));
        setNotes(receipt.notes || '');
    }, [receipt]);

    const save = async () => {
        if (!receipt || !allocationReady(allocation, categories)) return;
        setSaving(true);
        try {
            await updateReceipt(receipt.id, {
                ...allocationChanges(allocation),
                merchant: merchant.trim() || null,
                amount: amount || null,
                transaction_date: transactionDate || null,
                responsible_user_id: responsibleUserId
                    ? Number(responsibleUserId)
                    : receipt.responsible_user_id,
                notes: notes.trim() || null,
            });
            notify('Receipt allocation updated', { type: 'success' });
            onSaved();
            onClose();
        } catch (err) {
            notify(err instanceof Error ? err.message : 'Update failed', {
                type: 'error',
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={!!receipt} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Complete receipt allocation</DialogTitle>
            <DialogContent>
                <Stack spacing={2} mt={1}>
                    <AllocationFields
                        value={allocation}
                        onChange={setAllocation}
                        categories={categories}
                        showClassNote
                    />
                    <TextField
                        fullWidth
                        label="Merchant"
                        value={merchant}
                        onChange={event => setMerchant(event.target.value)}
                    />
                    <TextField
                        fullWidth
                        label="Amount"
                        type="number"
                        value={amount}
                        inputProps={{ min: 0, step: '0.01' }}
                        onChange={event => setAmount(event.target.value)}
                    />
                    <TextField
                        fullWidth
                        label="Transaction date"
                        type="date"
                        value={transactionDate}
                        InputLabelProps={{ shrink: true }}
                        onChange={event =>
                            setTransactionDate(event.target.value)
                        }
                    />
                    <FormControl fullWidth>
                        <InputLabel>Responsible employee</InputLabel>
                        <Select
                            value={responsibleUserId}
                            label="Responsible employee"
                            onChange={event =>
                                setResponsibleUserId(event.target.value)
                            }
                        >
                            {users.map(user => (
                                <MenuItem key={user.id} value={String(user.id)}>
                                    {user.name || user.email}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label="Notes"
                        value={notes}
                        onChange={event => setNotes(event.target.value)}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disabled={saving || !allocationReady(allocation, categories)}
                    onClick={() => void save()}
                >
                    Save allocation
                </Button>
            </DialogActions>
        </Dialog>
    );
}
